import React, { useState } from "react";

import { Dropdown, Row } from "react-bootstrap";

import classNames from "classnames";
import { range } from "lodash";
import { ReactSVG } from "react-svg";

import { ArrowIcon } from "@assets/icons";

import styles from "./Pager.module.scss";

type SetPageCallback = React.Dispatch<React.SetStateAction<number>>;

interface PagerProps {
  page: number;
  totalItems: number;
  totalPages: number;
  pageSize: number;
  setPage: SetPageCallback;
}

type PageButtonProps = Omit<PagerProps, "pageSize"> & {
  currentPage: number;
};

export const Pager = (props: Omit<PagerProps, "pageSize">) => {
  const { page, totalPages, setPage } = props;

  const currentPage = totalPages === 0 ? 0 : page;
  return (
    <div className="d-flex align-items-center gap-2">
      <button onClick={() => setPage(currentPage - 1)} type="button" disabled={currentPage <= 1}>
        Previous
      </button>
      <button onClick={() => setPage(currentPage + 1)} type="button" disabled={currentPage >= totalPages}>
        Next
      </button>
      <div>
        Page {currentPage}/{totalPages}
      </div>
    </div>
  );
};

const PageSelector = (props: Omit<PagerProps, "pageSize">) => {
  const { page, totalPages, setPage } = props;
  const [isToggleOpen, setIsToggleOpen] = useState(false);

  return (
    <Dropdown className="customized-dropdown" onToggle={() => setIsToggleOpen((prev) => !prev)}>
      <Dropdown.Toggle variant="none" className={styles["customized-dropdown-toggle"]}>
        <div className="d-flex gap-2 align-items-center">
          {page}
          <ReactSVG
            className={classNames({
              [styles["rotate-icon"]]: !isToggleOpen,
              [styles["normal-icon"]]: isToggleOpen,
            })}
            src={ArrowIcon}
          />
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu>
        {range(1, totalPages + 1).map((p) => (
          <Dropdown.Item key={p} onClick={() => setPage(p)}>
            {p}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export const PageSelect = (props: PagerProps) => {
  const { page, totalItems, totalPages, pageSize } = props;

  const itemIndex = (page - 1) * pageSize;

  return (
    <div
      className={classNames(
        styles["info-header"],
        "d-flex justify-content-between w-100 gap-2 px-4 pb-3 mb-3 align-items-center b4",
      )}
    >
      <div>
        {itemIndex + 1} to {Math.min(itemIndex + pageSize, totalItems)} of {totalItems}
      </div>
      <div className="d-flex align-items-center gap-2 b5 medium">
        Page <PageSelector {...props} /> of {totalPages}
      </div>
    </div>
  );
};

const PageButton = (page: number, currentPage: number, setPage: SetPageCallback) => {
  if (page === -1) {
    return (
      <span key={page} className={styles["ellipsis"]}>
        ...
      </span>
    );
  }
  const atCurrentPage = page === currentPage;
  return (
    <button
      key={page}
      onClick={() => setPage(page)}
      type="button"
      className={atCurrentPage ? classNames(styles["btn-selected"]) : ""}
    >
      {page}
    </button>
  );
};

// This function builds a list of page numbers to create page navigation buttons.
// This is by calculating a "sliding window" or min/max page number that is clamped
// to the min/max page number. For example if we wanted to create a 5 button "window"
// that is centered around page 2, we would return the set of pages: [1, 2, 3, 4, 5]
const pagesToShow = (page: number, totalPages: number): number[] => {
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    return range(1, totalPages + 1);
  }

  const pages: number[] = [];
  pages.push(1); // Always show the first page

  if (page > 3) {
    pages.push(-1); // Add ellipsis if current page is far from the start
  }

  const startPage = Math.max(2, page - 1);
  const endPage = Math.min(totalPages - 1, page + 1);

  for (let p = startPage; p <= endPage; p++) {
    pages.push(p);
  }

  if (page < totalPages - 2) {
    pages.push(-1); // Add ellipsis if current page is far from the end
  }

  pages.push(totalPages); // Always show the last page

  return pages;
};

const PageButtons = (props: PageButtonProps) => {
  const { page, totalPages, setPage, currentPage } = props;

  return <>{pagesToShow(page, totalPages).map((p) => PageButton(p, currentPage, setPage))}</>;
};

export const HorizontalButtonPager = (props: Omit<PagerProps, "pageSize">) => {
  const { page, totalPages, setPage } = props;
  const currentPage = totalPages === 0 ? 0 : page;

  if (currentPage === 0 || totalPages === 1) {
    return <></>;
  }

  return (
    <Row className={classNames(styles["button-row"])}>
      <button onClick={() => setPage(1)} type="button" disabled={currentPage <= 1}>
        {"<<"}
      </button>
      <button onClick={() => setPage(currentPage - 1)} type="button" disabled={currentPage <= 1}>
        {"<"}
      </button>
      <PageButtons {...props} currentPage={currentPage} />
      <button onClick={() => setPage(currentPage + 1)} type="button" disabled={currentPage >= totalPages}>
        {">"}
      </button>
      <button onClick={() => setPage(totalPages)} type="button" disabled={currentPage >= totalPages}>
        {">>"}
      </button>
    </Row>
  );
};
